import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  type Connection,
  type Edge,
  type Node,
  type NodeMouseHandler,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { LayoutIcon, Redo2, Undo2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useClassifierConfigList } from '@/hooks/useClassifierConfigs';
import { useQualityConfigList } from '@/hooks/useQualityConfigs';
import { useReviewConfigs } from '@/hooks/useReviewConfigs';
import { analyticsApi } from '@/lib/api/analytics.api';
import { dashboardConfigApi } from '@/lib/api/dashboard-config.api';

import { ImportExportToolbar } from './ImportExportToolbar';
import { NodePalette } from './NodePalette';
import { AgentNode } from './nodes/AgentNode';
import { BookingNode } from './nodes/BookingNode';
import { CallApiNode } from './nodes/CallApiNode';
import { CampaignNode } from './nodes/CampaignNode';
import { CollectNode } from './nodes/CollectNode';
import { ConditionNode } from './nodes/ConditionNode';
import { CreatePaymentNode } from './nodes/CreatePaymentNode';
import { EndNode } from './nodes/EndNode';
import { EnrichDataNode } from './nodes/EnrichDataNode';
import { FunctionNode } from './nodes/FunctionNode';
import { GetAnalyticsNode } from './nodes/GetAnalyticsNode';
import { GetQualityIssuesNode } from './nodes/GetQualityIssuesNode';
import { HandoffNode } from './nodes/HandoffNode';
import { NotificationNode } from './nodes/NotificationNode';
import { OutboundCallNode } from './nodes/OutboundCallNode';
import { ReturnNode } from './nodes/ReturnNode';
import { RunClassifierNode } from './nodes/RunClassifierNode';
import { RunQualityNode } from './nodes/RunQualityNode';
import { RunReviewNode } from './nodes/RunReviewNode';
import { ScheduleWorkflowNode } from './nodes/ScheduleWorkflowNode';
import { SendEmailNode } from './nodes/SendEmailNode';
import { SendSmsNode } from './nodes/SendSmsNode';
import { StartNode } from './nodes/StartNode';
import { StockNode } from './nodes/StockNode';
import { TrackingLinkNode } from './nodes/TrackingLinkNode';
import { UpdateCallNode } from './nodes/UpdateCallNode';
import { UpdateContactNode } from './nodes/UpdateContactNode';
import { NodeConfigPanel } from './panels/NodeConfigPanel';
import {
  autoLayout,
  DEFAULT_START_NODE,
  generateNodeId,
  getDefaultNodeData,
  getStartLabel,
} from './workflow-utils';
import { WorkflowHelpGuide } from './WorkflowHelpGuide';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from './VariablePicker';
import type {
  CollectNodeData,
  PostCallWorkflow,
  WorkflowEdge,
  WorkflowNode,
} from '@repo/shared-types';

const nodeTypes = {
  start: StartNode,
  condition: ConditionNode,
  send_email: SendEmailNode,
  send_sms: SendSmsNode,
  update_call: UpdateCallNode,
  update_contact: UpdateContactNode,
  enrich_data: EnrichDataNode,
  agent: AgentNode,
  run_review: RunReviewNode,
  run_classifier: RunClassifierNode,
  run_quality: RunQualityNode,
  call_api: CallApiNode,
  get_analytics: GetAnalyticsNode,
  get_quality_issues: GetQualityIssuesNode,
  booking: BookingNode,
  outbound_call: OutboundCallNode,
  notification: NotificationNode,
  stock: StockNode,
  collect: CollectNode,
  campaign: CampaignNode,
  handoff: HandoffNode,
  schedule_workflow: ScheduleWorkflowNode,
  function: FunctionNode,
  create_payment: CreatePaymentNode,
  tracking_link: TrackingLinkNode,
  return: ReturnNode,
  end: EndNode,
};

interface WorkflowCanvasProps {
  value: PostCallWorkflow | null;
  onChange: (workflow: PostCallWorkflow) => void;
  responseFieldsSchema?: Record<string, unknown>;
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

function toFlowNodes(workflowNodes: WorkflowNode[]): Node[] {
  return workflowNodes.map((n) => ({
    id: n.id,
    type: n.type,
    position: n.position,
    data: n.data,
    deletable: n.type !== 'start',
  }));
}

function toFlowEdges(workflowEdges: WorkflowEdge[]): Edge[] {
  return workflowEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle,
    type: 'smoothstep',
    style: { strokeWidth: 2 },
    label:
      e.sourceHandle === 'true'
        ? 'True'
        : e.sourceHandle === 'false'
          ? 'False'
          : e.sourceHandle === 'sent'
            ? 'Sent'
            : e.sourceHandle === 'failed'
              ? 'Failed'
              : undefined,
    labelStyle: { fontSize: 10, fontWeight: 600 },
    labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.9 },
  }));
}

function fromFlowNodes(nodes: Node[]): WorkflowNode[] {
  return nodes.map((n) => ({
    id: n.id,
    type: n.type as WorkflowNode['type'],
    position: n.position,
    data: n.data,
  }));
}

function fromFlowEdges(edges: Edge[]): WorkflowEdge[] {
  return edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle ?? undefined,
  }));
}

function WorkflowCanvasInner({
  value,
  onChange,
  responseFieldsSchema,
  triggerType = 'call',
}: WorkflowCanvasProps) {
  const { screenToFlowPosition } = useReactFlow();
  const [paletteOpen, setPaletteOpen] = useState(true);

  const startLabel = getStartLabel(triggerType);
  const initialNodes =
    value && value.nodes.length > 0
      ? value.nodes.map((n) =>
          n.type === 'start' ? { ...n, data: { ...n.data, label: startLabel } } : n,
        )
      : [{ ...DEFAULT_START_NODE, data: { label: startLabel } }];
  const initialEdges = value?.edges ?? [];

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(toFlowNodes(initialNodes));
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(toFlowEdges(initialEdges));
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null);

  const { data: reviewConfigs } = useReviewConfigs();
  const { data: classifierConfigs } = useClassifierConfigList();
  const { data: qualityConfigs } = useQualityConfigList();

  const reviewNodeMetas: ReviewNodeMeta[] = useMemo(() => {
    const configs = reviewConfigs ?? [];
    return nodes
      .filter((n) => n.type === 'run_review')
      .map((n) => {
        const outputKey = String(n.data.outputKey || 'review');
        const configId = String(n.data.reviewConfigId || '');
        const config = configs.find((c) => c.id === configId);
        return {
          outputKey,
          schema: config?.response_fields_schema as Record<string, unknown> | undefined,
          isAudioMode: config?.review_input_mode === 'AUDIO',
        };
      });
  }, [nodes, reviewConfigs]);

  // run_classifier nodes → autocomplete suggests `<outputKey>.labels.<bool>` /
  // `<outputKey>.data.<scalar>` based on the referenced classifier_config's
  // taxonomy. Falls back to the is_default config if the node has no explicit
  // classifierConfigId set.
  const classifierNodeMetas: ClassifierNodeMeta[] = useMemo(() => {
    const configs = classifierConfigs ?? [];
    const defaultCfg = configs.find((c) => c.is_default);
    return nodes
      .filter((n) => n.type === 'run_classifier')
      .map((n) => {
        const outputKey = String(n.data.outputKey || 'classification');
        const configId = String(n.data.classifierConfigId || '');
        const config = configs.find((c) => c.id === configId) ?? defaultCfg;
        return {
          outputKey,
          taxonomy: config?.taxonomy,
        };
      });
  }, [nodes, classifierConfigs]);

  // run_quality nodes → same shape, source is the quality_config's `rubric`,
  // bools land under `<outputKey>.flags.<key>`, plus the top-level `reasoning`.
  const qualityNodeMetas: QualityNodeMeta[] = useMemo(() => {
    const configs = qualityConfigs ?? [];
    const defaultCfg = configs.find((c) => c.is_default);
    return nodes
      .filter((n) => n.type === 'run_quality')
      .map((n) => {
        const outputKey = String(n.data.outputKey || 'quality');
        const configId = String(n.data.qualityConfigId || '');
        const config = configs.find((c) => c.id === configId) ?? defaultCfg;
        return {
          outputKey,
          rubric: config?.rubric,
        };
      });
  }, [nodes, qualityConfigs]);

  // agent nodes → suggestions come straight off the node's inline outputSchema
  // (no config row to look up), spread at the top level of `<outputKey>`.
  const agentNodeMetas: AgentNodeMeta[] = useMemo(
    () =>
      nodes
        .filter((n) => n.type === 'agent')
        .map((n) => ({
          outputKey: String(n.data.outputKey || 'agent'),
          outputSchema: n.data.outputSchema as Record<string, unknown> | undefined,
        })),
    [nodes],
  );

  const { data: dashboardConfig } = useQuery({
    queryKey: ['dashboard-config-for-variables'],
    queryFn: () => analyticsApi.getDashboardConfig(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: metricOptions } = useQuery({
    queryKey: ['metric-options-for-variables'],
    queryFn: () => dashboardConfigApi.getMetricOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const analyticsNodeMetas: AnalyticsNodeMeta[] = useMemo(() => {
    const metricKeys = dashboardConfig?.metrics?.length
      ? dashboardConfig.metrics
      : (metricOptions?.map((m) => m.key) ?? []);
    return nodes
      .filter((n) => n.type === 'get_analytics')
      .map((n) => ({
        outputKey: String(n.data.outputKey || 'analytics'),
        metricKeys,
      }));
  }, [nodes, dashboardConfig, metricOptions]);

  // Undo/redo history
  const historyRef = useRef<{ nodes: Node[]; edges: Edge[] }[]>([]);
  const historyIndexRef = useRef(-1);
  const isUndoRedoRef = useRef(false);

  const saveHistory = useCallback(() => {
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }
    const state = { nodes: [...nodes], edges: [...edges] };
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(state);
    historyIndexRef.current = historyRef.current.length - 1;
    // Cap at 50 entries
    if (historyRef.current.length > 50) {
      historyRef.current.shift();
      historyIndexRef.current--;
    }
  }, [nodes, edges]);

  useEffect(() => {
    const label = getStartLabel(triggerType);
    setNodes((nds) =>
      nds.map((n) => (n.type === 'start' ? { ...n, data: { ...n.data, label } } : n)),
    );
  }, [triggerType, setNodes]);

  // Save history when nodes/edges change (debounced)
  useEffect(() => {
    const timer = setTimeout(saveHistory, 300);
    return () => clearTimeout(timer);
  }, [nodes, edges, saveHistory]);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current--;
    isUndoRedoRef.current = true;
    const state = historyRef.current[historyIndexRef.current];
    setNodes(state.nodes);
    setEdges(state.edges);
  }, [setNodes, setEdges]);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current++;
    isUndoRedoRef.current = true;
    const state = historyRef.current[historyIndexRef.current];
    setNodes(state.nodes);
    setEdges(state.edges);
  }, [setNodes, setEdges]);

  // Sync back to parent — skip on first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onChange({ nodes: fromFlowNodes(nodes), edges: fromFlowEdges(edges) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: `edge_${Date.now()}`,
            type: 'smoothstep',
            style: { strokeWidth: 2 },
          },
          eds,
        ),
      );
    },
    [setEdges],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/workflow-node-type');
      if (!type) return;

      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const newNode: Node = {
        id: generateNodeId(),
        type,
        position,
        data: getDefaultNodeData(type),
        deletable: type !== 'start',
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [screenToFlowPosition, setNodes],
  );

  const onNodeClick: NodeMouseHandler = useCallback((_, flowNode) => {
    setSelectedNode({
      id: flowNode.id,
      type: flowNode.type as WorkflowNode['type'],
      position: flowNode.position,
      data: flowNode.data,
    });
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onNodeDataChange = useCallback(
    (nodeId: string, data: Record<string, unknown>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n)),
      );
      setSelectedNode((prev) =>
        prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...data } } : prev,
      );
    },
    [setNodes],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Undo: Ctrl+Z / Cmd+Z
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
        return;
      }
      // Redo: Ctrl+Y / Ctrl+Shift+Z / Cmd+Shift+Z
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === 'y' || (event.key === 'z' && event.shiftKey))
      ) {
        event.preventDefault();
        redo();
        return;
      }
    },
    [undo, redo],
  );

  const handleAutoLayout = useCallback(() => {
    const currentNodes = fromFlowNodes(nodes);
    const currentEdges = fromFlowEdges(edges);
    const laid = autoLayout(currentNodes, currentEdges);
    setNodes(toFlowNodes(laid));
  }, [nodes, edges, setNodes]);

  const handleImport = useCallback(
    (workflow: PostCallWorkflow) => {
      setNodes(toFlowNodes(workflow.nodes));
      setEdges(toFlowEdges(workflow.edges));
      setSelectedNode(null);
    },
    [setNodes, setEdges],
  );

  const handleExport = useCallback(() => {
    const workflow: PostCallWorkflow = {
      nodes: fromFlowNodes(nodes),
      edges: fromFlowEdges(edges),
    };
    const blob = new Blob([JSON.stringify(workflow, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow-export-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [nodes, edges]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden" onKeyDown={onKeyDown} tabIndex={0}>
      {/* Top toolbar */}
      <div className="flex items-center justify-end border-b px-3 py-2">
        <div className="flex items-center gap-1.5">
          <ImportExportToolbar onImport={handleImport} onExport={handleExport} />
          <Button size="sm" variant="outline" onClick={undo} title="Undo (Ctrl+Z)">
            <Undo2 size={14} />
          </Button>
          <Button size="sm" variant="outline" onClick={redo} title="Redo (Ctrl+Y)">
            <Redo2 size={14} />
          </Button>
          <Button size="sm" variant="outline" onClick={handleAutoLayout}>
            <LayoutIcon size={14} className="mr-1" />
            Auto Layout
          </Button>
          <WorkflowHelpGuide />
        </div>
      </div>
      {/* Left palette + Canvas + config panel */}
      <div className="flex flex-1 overflow-hidden">
        <NodePalette open={paletteOpen} onToggle={() => setPaletteOpen(!paletteOpen)} />
        <div className="relative flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{ type: 'smoothstep', style: { strokeWidth: 2 } }}
            deleteKeyCode={['Backspace', 'Delete']}
            edgesReconnectable
            edgesFocusable
          >
            <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
            <Controls />
          </ReactFlow>
        </div>
        {selectedNode && (
          <NodeConfigPanel
            selectedNode={selectedNode}
            onNodeDataChange={onNodeDataChange}
            collectNodeDatas={nodes
              .filter((n) => n.type === 'collect')
              .map((n) => n.data as unknown as CollectNodeData)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        )}
      </div>
    </div>
  );
}

export const WorkflowCanvas = (props: WorkflowCanvasProps) => {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner {...props} />
    </ReactFlowProvider>
  );
};
